import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Inbox, CheckCircle2, XCircle, Clock, Mail, Search, Loader2, Globe, AlertTriangle, Send, Phone, UserCog, HelpCircle, GitBranch, AlertOctagon, CalendarClock, Users2, ShieldCheck, ClipboardList, ExternalLink, Settings } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { formatDate, formatDateTime, formatTime, formatTimeRangeUhr, vokalSpelling, formatDeNumber } from '@/lib/utils';
import { formatCourseOfferLine } from '@/pages/Public/wizard/courseOfferLabel';
import { formatAvailability } from '@/lib/availability';
import { composePostalAddress, composeStreetLine } from '@/lib/addressLine';
import { statusLabel } from '@/lib/deLabels';
import { registrationToEnrollmentPrefill, storeEnrollmentPrefill } from '@/lib/registrationToEnrollmentPrefill';
import RegistrationMatchPanel from '@/components/registrations/RegistrationMatchPanel';

const EMPTY_MATCH_DECISION = {
  studentId: null,
  guardianId: null,
  chooseNew: false,
  fieldUpdates: [],
  updateTarget: null,
};

function buildProfileUpdates(detail, decision) {
  const keys = new Set((decision.fieldUpdates || []).map((row) => row.key));
  if (!keys.size) return null;
  const payload = detail?.payload || {};
  const payer = payload.payer || {};
  const student = payload.student || {};
  const target = decision.updateTarget === 'student' ? 'student' : 'guardian';
  const source = target === 'student' ? student : payer;
  const addressSource = target === 'student' && (student.address_same || !composeStreetLine(student))
    ? payer
    : source;
  const updates = { target };
  if (keys.has('address')) {
    updates.address = composeStreetLine(addressSource) || String(addressSource.address || '').trim();
    updates.postal_code = addressSource.postal_code || '';
    updates.city = addressSource.city || '';
  }
  if (keys.has('phone')) updates.phone = source.phone || payer.phone || '';
  if (keys.has('email')) updates.email = source.email || payer.email || '';
  if (keys.has('iban_last4') && target === 'guardian') {
    const sepa = payload.sepa || {};
    updates.replace_bank = true;
    updates.iban = sepa.iban || '';
    updates.bic = sepa.bic || '';
    updates.account_holder = sepa.account_holder || '';
  }
  return updates;
}
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import DateInput from '@/components/ui/date-input';
import TimeInput from '@/components/ui/time-input';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';

const STATUS_TABS = [
  { key: 'confirmed', label: 'Bestätigt', icon: Inbox },
  { key: 'pending', label: 'Warten auf E-Mail-Bestätigung', icon: Clock },
  { key: 'approved', label: 'Genehmigt', icon: CheckCircle2 },
  { key: 'rejected', label: 'Abgelehnt', icon: XCircle },
];

const WORKFLOW_OPTIONS = [
  { key: 'calling_parents',  label: 'Eltern angerufen',     icon: Phone,      cls: 'bg-blue-100 text-blue-800 hover:bg-blue-200',         active: 'bg-blue-600 text-white hover:bg-blue-700' },
  { key: 'waiting_teacher',  label: 'Wartet auf Lehrkraft', icon: UserCog,    cls: 'bg-brand-red/10 text-brand-red hover:bg-brand-red/20',   active: 'bg-brand-red text-white hover:bg-brand-red/90' },
  { key: 'open_question',    label: 'Offene Frage',         icon: HelpCircle, cls: 'bg-rose-100 text-rose-800 hover:bg-rose-200',         active: 'bg-rose-600 text-white hover:bg-rose-700' },
];

function workflowMeta(value) {
  return WORKFLOW_OPTIONS.find((o) => o.key === value);
}

export default function AdminRegistrationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('confirmed');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, confirmed: 0, approved: 0, rejected: 0 });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [matching, setMatching] = useState(null);
  const [personMatches, setPersonMatches] = useState(null);
  const [matchDecision, setMatchDecision] = useState(EMPTY_MATCH_DECISION);
  const [registrationFilters, setRegistrationFilters] = useState({
    student: '',
    instrument: '',
    payer: '',
    email: '',
    received: '',
  });
  const [appliedRegistrationFilters, setAppliedRegistrationFilters] = useState({});
  const [comms, setComms] = useState(null);
  const [resendingConfirm, setResendingConfirm] = useState(false);

  const assignCourse = async (courseId, courseName) => {
    try {
      await api.post(`/admin/registrations/${selected}/assign-course`, { course_id: courseId });
      toast.success(`Schüler in „${courseName}" eingeteilt`);
      api.get(`/admin/registrations/${selected}/matching-slots`).then((r) => setMatching(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einteilung fehlgeschlagen');
    }
  };
  const [approving, setApproving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approveOptions, setApproveOptions] = useState(null);
  const [approveTariffId, setApproveTariffId] = useState('');
  const [approveTeacherId, setApproveTeacherId] = useState('');
  const [approveRoomId, setApproveRoomId] = useState('');
  const [approveStartDate, setApproveStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [approveSkipContract, setApproveSkipContract] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [sla, setSla] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  const [workflowFilter, setWorkflowFilter] = useState('all'); // all | calling_parents | waiting_teacher | open_question | none
  const [f7Filter, setF7Filter] = useState('all'); // all | waiting_teacher | teacher_proposed | waiting_parent | teacher_declined | parent_declined | manual_intervention
  const [groupWaitlistFilter, setGroupWaitlistFilter] = useState(false);
  const [funnel, setFunnel] = useState(null);

  // Phase F4 — Slot-Bestätigungsmail (Preview + Send)
  const [slotMailOpen, setSlotMailOpen] = useState(false);
  const [slotMailLoading, setSlotMailLoading] = useState(false);
  const [slotMailPreview, setSlotMailPreview] = useState(null);
  const [slotMailSendParent, setSlotMailSendParent] = useState(true);
  const [slotMailSendTeacher, setSlotMailSendTeacher] = useState(true);
  const [slotMailSending, setSlotMailSending] = useState(false);
  const [coordBusy, setCoordBusy] = useState(false);
  const [coordTeachers, setCoordTeachers] = useState([]);
  const [coordTeacherId, setCoordTeacherId] = useState('');
  const [coordDate, setCoordDate] = useState('');
  const [coordStart, setCoordStart] = useState('');
  const [coordDuration, setCoordDuration] = useState(45);

  const loadSla = useCallback(async () => {
    try {
      const r = await api.get('/admin/registrations/sla');
      setSla(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/registrations/summary');
      setSummary(r.data);
    } catch (e) {
      // ignored — sidebar keeps previous counters
    }
  }, []);

  const loadFunnel = useCallback(async () => {
    try {
      const r = await api.get('/admin/registrations/funnel-summary');
      setFunnel(r.data);
    } catch (e) {
      // non-blocking
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab };
      if (search) params.search = search;
      Object.entries(appliedRegistrationFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      if (workflowFilter && workflowFilter !== 'all') params.workflow = workflowFilter;
      if (groupWaitlistFilter) params.group_waitlist = true;
      // F7 Workflow-Phase-Filter (nur bei offenen Tabs sinnvoll)
      if ((tab === 'confirmed' || tab === 'pending') && f7Filter && f7Filter !== 'all') {
        if (f7Filter === 'waiting_teacher') params.teacher_slot = 'pending';
        else if (f7Filter === 'teacher_proposed') params.teacher_slot = 'proposed_alternatives';
        else if (f7Filter === 'teacher_declined') params.teacher_slot = 'declined';
        else if (f7Filter === 'waiting_parent') params.parent_choice = 'pending';
        else if (f7Filter === 'parent_declined') params.parent_choice = 'declined';
        else if (f7Filter === 'manual_intervention') params.manual_intervention_only = true;
      }
      const r = await api.get('/admin/registrations', { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Liste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [tab, search, workflowFilter, f7Filter, groupWaitlistFilter, appliedRegistrationFilters]);

  useEffect(() => { loadList(); loadSummary(); loadSla(); loadFunnel(); }, [loadList, loadSummary, loadSla, loadFunnel]);

  const dispatchSlaReminders = async () => {
    if (!window.confirm('Erinnerungs-Mails an alle Admins senden?\n\nDie Mails enthalten KEINE personenbezogenen Daten — nur Anzahl + Link zur Inbox. Doppel-Mails werden 24 h lang automatisch unterdrückt.')) return;
    setDispatching(true);
    try {
      const r = await api.post('/admin/registrations/sla/dispatch');
      const { sent, skipped, overdue } = r.data;
      if (overdue === 0) {
        toast.success('Aktuell nichts überfällig — keine Erinnerungen versendet');
      } else if (sent === 0 && skipped > 0) {
        toast.success(`${skipped} Erinnerungen geskippt (Throttle aktiv)`);
      } else {
        toast.success(`${sent} Erinnerung(en) verschickt, ${skipped} geskippt`);
      }
      await loadSla();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setDispatching(false);
    }
  };

  const openDetail = async (id, persistUrl = true) => {
    setSelected(id);
    setDetailLoading(true);
    setMatching(null);
    setPersonMatches(null);
    setMatchDecision(EMPTY_MATCH_DECISION);
    setComms(null);
    setCoordTeachers([]);
    setCoordTeacherId('');
    setCoordDate('');
    setCoordStart('');
    setCoordDuration(45);
    if (persistUrl) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('id', id);
        return next;
      }, { replace: true });
    }
    api.get(`/admin/registrations/${id}/matching-slots`).then((r) => setMatching(r.data)).catch(() => {});
    api.get(`/admin/registrations/${id}/matches`).then((r) => setPersonMatches(r.data)).catch(() => setPersonMatches({ matches: [] }));
    api.get(`/admin/registrations/${id}/communications`).then((r) => setComms(r.data)).catch(() => setComms({ items: [] }));
    try {
      const r = await api.get(`/admin/registrations/${id}`);
      setDetail(r.data);
      const slot = r.data?.payload?.preferred_slot;
      const mins = String(r.data?.payload?.teaching_type || '').match(/(\d+)\s*Min/i);
      if (mins) setCoordDuration(Number(mins[1]) || 45);
      if (slot?.teacher_id) setCoordTeacherId(String(slot.teacher_id));
      if (slot?.date) setCoordDate(slot.date);
      if (slot?.start_time) setCoordStart(String(slot.start_time).slice(0, 5));
      if (slot?.duration_minutes) setCoordDuration(Number(slot.duration_minutes) || 45);
      if (!slot) {
        try {
          const opts = await api.get(`/admin/registrations/${id}/approve-options`);
          setCoordTeachers(opts.data?.teachers || []);
          if (opts.data?.suggested_teacher_id) setCoordTeacherId(opts.data.suggested_teacher_id);
        } catch (e) { /* optional */ }
      }
    } catch (e) {
      toast.error('Detail konnte nicht geladen werden');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setPersonMatches(null);
    setMatchDecision(EMPTY_MATCH_DECISION);
    setComms(null);
    setApproveOpen(false);
    setRejectOpen(false);
    setApproveNotes('');
    setRejectReason('');
    setApproveOptions(null);
    setApproveTariffId('');
    setApproveTeacherId('');
    setApproveRoomId('');
    setApproveSkipContract(false);
    setCoordTeachers([]);
    setCoordTeacherId('');
    setCoordDate('');
    setCoordStart('');
    setCoordDuration(45);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('id');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) openDetail(id, false);
    // einmalig aus der URL wiederherstellen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRowProps } = useListKeyboardNav({
    items,
    onSelect: (r) => {
      window.open(`/registrations-inbox?id=${encodeURIComponent(r.id)}`, '_blank', 'noopener,noreferrer');
    },
    enabled: !selected && !approveOpen && !rejectOpen,
  });

  const setWorkflowStatus = async (value) => {
    if (!detail) return;
    try {
      const r = await api.patch(`/admin/registrations/${detail.id}/workflow`, { workflow_status: value });
      setDetail(r.data);
      await loadList();
      if (value === 'calling_parents' && !r.data?.payload?.preferred_slot) {
        toast.message('Eltern angerufen — jetzt den vereinbarten Termin eintragen und die Lehrkraft anfragen.');
        document.getElementById('registrations-assign-slot')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Workflow-Status konnte nicht gesetzt werden');
    }
  };

  const openApproveDialog = async () => {
    if (!detail) return;
    setApproveOpen(true);
    setApproveOptions(null);
    setApproveTariffId('');
    setApproveTeacherId('');
    setApproveRoomId('');
    setApproveSkipContract(false);
    setApproveStartDate(new Date().toISOString().slice(0, 10));
    try {
      const r = await api.get(`/admin/registrations/${detail.id}/approve-options`);
      setApproveOptions(r.data);
      setApproveTariffId(r.data.suggested_tariff_id || '');
      setApproveTeacherId(r.data.suggested_teacher_id || '');
    } catch (e) {
      setApproveOptions({ tariffs: [], teachers: [], suggested_tariff_id: null, suggested_teacher_id: null });
    }
  };

  const approve = async () => {
    if (!detail) return;
    setApproving(true);
    try {
      const body = {
        notes: approveNotes || null,
        skip_contract: approveSkipContract,
      };
      if (!approveSkipContract && approveTariffId) {
        body.tariff_id = approveTariffId;
        if (approveTeacherId) body.teacher_id = approveTeacherId;
        if (approveRoomId) body.room_id = approveRoomId;
        if (approveStartDate) body.start_date = approveStartDate;
      }
      const r = await api.post(`/admin/registrations/${detail.id}/approve`, body);
      const parts = [];
      if (r.data?.created_contract_id) parts.push('Vertrag');
      if (r.data?.created_course_id) parts.push('Kurs');
      if (r.data?.created_enrollment_id) parts.push('Enrollment');
      if (parts.length > 0) {
        toast.success(`Anmeldung genehmigt — ${parts.join(' + ')} angelegt`);
      } else {
        toast.success('Anmeldung genehmigt (nur Stammdaten)');
      }
      await loadList(); await loadSummary(); await loadFunnel();
      closeDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Genehmigen fehlgeschlagen');
    } finally {
      setApproving(false);
    }
  };

  const reject = async () => {
    if (!detail) return;
    if ((rejectReason || '').trim().length < 3) {
      toast.error('Bitte Grund angeben');
      return;
    }
    setApproving(true);
    try {
      await api.post(`/admin/registrations/${detail.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Anmeldung abgelehnt');
      await loadList(); await loadSummary(); await loadFunnel();
      closeDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ablehnen fehlgeschlagen');
    } finally {
      setApproving(false);
    }
  };

  const startTeacherCoordination = async (withAssignedSlot = false) => {
    if (!detail) return;
    const body = {};
    if (withAssignedSlot || !detail.payload?.preferred_slot) {
      const missing = [
        !coordTeacherId && 'Lehrkraft',
        !coordDate && 'Datum',
        !coordStart && 'Uhrzeit',
      ].filter(Boolean);
      if (missing.length > 0) {
        toast.error(`Bitte noch ausfüllen: ${missing.join(', ')}`);
        return;
      }
      body.teacher_id = coordTeacherId;
      body.date = coordDate;
      body.start_time = coordStart;
      body.duration_minutes = Number(coordDuration) || 45;
    }
    setCoordBusy(true);
    try {
      const r = await api.post(`/admin/registrations/${detail.id}/start-teacher-coordination`, body);
      setDetail(r.data);
      toast.success('Lehrkraft wurde von der Verwaltung angefragt');
      await loadList();
      await loadFunnel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anfrage an die Lehrkraft fehlgeschlagen');
    } finally {
      setCoordBusy(false);
    }
  };

  const forwardToParent = async () => {
    if (!detail) return;
    setCoordBusy(true);
    try {
      const r = await api.post(`/admin/registrations/${detail.id}/forward-to-parent`);
      setDetail(r.data);
      toast.success('Rückmeldung an den Anmelder weitergeleitet');
      await loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Weiterleiten fehlgeschlagen');
    } finally {
      setCoordBusy(false);
    }
  };

  const openManualEnroll = () => {
    if (!detail) return;
    if (personMatches?.matches?.length
      && !matchDecision.studentId
      && !matchDecision.guardianId
      && !matchDecision.chooseNew) {
      toast.error('Bitte zuerst entscheiden: vorhandene Person verknüpfen oder neu anlegen.');
      return;
    }
    const prefill = registrationToEnrollmentPrefill(detail);
    if (!prefill) {
      toast.error('Anmeldung konnte nicht übernommen werden');
      return;
    }
    storeEnrollmentPrefill({
      fromRegistration: true,
      registrationId: detail.id,
      enrollmentPrefill: {
        ...prefill,
        existingStudentId: matchDecision.studentId,
        existingGuardianId: matchDecision.guardianId,
        profileUpdates: buildProfileUpdates(detail, matchDecision),
      },
    });
    window.open('/unterrichtsverwaltung', '_blank', 'noopener,noreferrer');
    toast.message('Unterrichtsverwaltung in neuem Tab geöffnet — diese Anmeldung bleibt hier offen.');
  };

  const openUnterrichtsverwaltung = () => {
    window.open('/unterrichtsverwaltung', '_blank', 'noopener,noreferrer');
  };

  const resendConfirmation = async () => {
    if (!detail) return;
    setResendingConfirm(true);
    try {
      const r = await api.post(`/admin/registrations/${detail.id}/resend-confirmation`);
      toast.success(`Bestätigung erneut an ${r.data?.recipient || detail.payer_email} gesendet`);
      const comm = await api.get(`/admin/registrations/${detail.id}/communications`);
      setComms(comm.data);
      const fresh = await api.get(`/admin/registrations/${detail.id}`);
      setDetail(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erneuter Versand fehlgeschlagen');
    } finally {
      setResendingConfirm(false);
    }
  };

  // Phase F4 — Slot-Bestätigungsmail: Preview + Send
  const openSlotMailDialog = async () => {
    if (!detail) return;
    setSlotMailOpen(true);
    setSlotMailLoading(true);
    setSlotMailPreview(null);
    try {
      const r = await api.get(`/admin/registrations/${detail.id}/slot-confirmation/preview`);
      setSlotMailPreview(r.data);
      setSlotMailSendParent(true);
      setSlotMailSendTeacher(!!r.data.has_teacher_email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voransicht konnte nicht geladen werden');
      setSlotMailOpen(false);
    } finally {
      setSlotMailLoading(false);
    }
  };

  const sendSlotMail = async () => {
    if (!detail) return;
    if (!slotMailSendParent && !slotMailSendTeacher) {
      toast.error('Bitte mindestens einen Empfänger wählen');
      return;
    }
    setSlotMailSending(true);
    try {
      const r = await api.post(`/admin/registrations/${detail.id}/slot-confirmation/send`, {
        send_parent: slotMailSendParent,
        send_teacher: slotMailSendTeacher,
      });
      const d = r.data;
      if (d.failed === 0) {
        toast.success(`${d.sent} Bestätigungs-Mail${d.sent === 1 ? '' : 's'} mit iCal-Einladung versendet`);
      } else {
        toast.error(`${d.sent} versendet, ${d.failed} fehlgeschlagen`);
      }
      setSlotMailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSlotMailSending(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="registrations-inbox">
        {!selected && (
        <>
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="registrations-inbox-title">
              <Globe className="w-7 h-7 text-brand-red" /> Online-Anmeldungen
            </h1>
            <p className="text-slate-600 mt-1">
              Öffentlich eingereichte Anmeldungen — prüfen, genehmigen oder ablehnen.
              {' '}
              <a href="/settings#online-anmeldung" className="text-brand-red hover:underline inline-flex items-center gap-1" data-testid="registrations-inbox-settings-link">
                <Settings className="h-3.5 w-3.5" /> Texte und Einstellungen
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadList(); } }}
                placeholder="Suche E-Mail, Name, Instrument…"
                className="pl-9 w-64"
                data-testid="registrations-inbox-search"
              />
            </div>
            <Button variant="outline" onClick={loadList} data-testid="registrations-inbox-refresh">Aktualisieren</Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          {/* SLA Banner */}
          {sla?.enabled && sla.overdue_count > 0 && (
            <div
              className="flex items-center gap-3 mb-4 p-3 bg-brand-red/5 border border-brand-red/20 rounded-lg"
              data-testid="registrations-sla-banner"
            >
              <AlertTriangle className="h-5 w-5 text-brand-red shrink-0" />
              <div className="flex-1 text-sm text-slate-800">
                <strong>{sla.overdue_count}</strong> Anmeldung(en) länger als <strong>{sla.sla_days} Werktag(en)</strong> unbearbeitet
                {sla.oldest_confirmed_age_days ? ` (älteste: ${sla.oldest_confirmed_age_days} Tag(e))` : ''}.
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setTab('confirmed'); }}
                data-testid="registrations-sla-show-overdue"
              >
                Überfällige zeigen
              </Button>
              <Button
                size="sm"
                onClick={dispatchSlaReminders}
                disabled={dispatching}
                data-testid="registrations-sla-dispatch"
              >
                {dispatching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Erinnerung jetzt senden
              </Button>
            </div>
          )}

          <TabsList data-testid="registrations-inbox-tabs">
            {STATUS_TABS.map((t) => {
              const count = summary[t.key] ?? 0;
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.key} value={t.key} data-testid={`registrations-inbox-tab-${t.key}`}>
                  <Icon className="h-4 w-4 mr-1" />
                  {t.label}
                  <Badge variant="secondary" className="ml-2" data-testid={`registrations-inbox-count-${t.key}`}>{count}</Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* F7 Funnel-Summary Widget (Slot-Verhandlungs-Statistik) */}
          {(tab === 'confirmed' || tab === 'pending') && funnel && funnel.total_active > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-br from-slate-50 to-slate-50/40 border border-slate-200 rounded-xl" data-testid="registrations-funnel-summary">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <GitBranch className="h-4 w-4 text-brand-red" />
                  Slot-Verhandlung · {funnel.total_active} offen
                </div>
                {funnel.needs_manual_intervention > 0 && (
                  <Badge className="gap-1 bg-red-100 text-red-900 hover:bg-red-100 border border-red-300" data-testid="registrations-funnel-manual-badge">
                    <AlertOctagon className="h-3 w-3" /> {funnel.needs_manual_intervention} Eskaliert
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <FunnelStat
                  icon={CalendarClock}
                  label="Wartet auf Lehrkraft"
                  value={funnel.teacher?.pending ?? 0}
                  active={f7Filter === 'waiting_teacher'}
                  onClick={() => setF7Filter(f7Filter === 'waiting_teacher' ? 'all' : 'waiting_teacher')}
                  tone="sky"
                  testId="registrations-funnel-waiting-teacher"
                />
                <FunnelStat
                  icon={Mail}
                  label="Lehrer schlägt Alt. vor"
                  value={funnel.teacher?.proposed_alternatives ?? 0}
                  active={f7Filter === 'teacher_proposed'}
                  onClick={() => setF7Filter(f7Filter === 'teacher_proposed' ? 'all' : 'teacher_proposed')}
                  tone="indigo"
                  testId="registrations-funnel-teacher-proposed"
                />
                <FunnelStat
                  icon={Users2}
                  label="Wartet auf Eltern"
                  value={funnel.parent?.pending ?? 0}
                  active={f7Filter === 'waiting_parent'}
                  onClick={() => setF7Filter(f7Filter === 'waiting_parent' ? 'all' : 'waiting_parent')}
                  tone="slate"
                  testId="registrations-funnel-waiting-parent"
                />
                <FunnelStat
                  icon={AlertOctagon}
                  label="Manuell zuweisen"
                  value={funnel.needs_manual_intervention ?? 0}
                  active={f7Filter === 'manual_intervention'}
                  onClick={() => setF7Filter(f7Filter === 'manual_intervention' ? 'all' : 'manual_intervention')}
                  tone="red"
                  testId="registrations-funnel-manual-intervention"
                />
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                <span className="text-slate-500">Weitere Filter:</span>
                <FunnelMiniPill
                  active={f7Filter === 'teacher_declined'}
                  onClick={() => setF7Filter(f7Filter === 'teacher_declined' ? 'all' : 'teacher_declined')}
                  label={`Lehrer abgelehnt (${funnel.teacher?.declined ?? 0})`}
                  testId="registrations-funnel-teacher-declined"
                />
                <FunnelMiniPill
                  active={f7Filter === 'parent_declined'}
                  onClick={() => setF7Filter(f7Filter === 'parent_declined' ? 'all' : 'parent_declined')}
                  label={`Eltern abgelehnt (${funnel.parent?.declined ?? 0})`}
                  testId="registrations-funnel-parent-declined"
                />
                {f7Filter !== 'all' && (
                  <button
                    type="button"
                    onClick={() => setF7Filter('all')}
                    className="ml-auto text-brand-red underline hover:text-brand-red"
                    data-testid="registrations-funnel-reset"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Workflow-Filter (nur für offene Tabs sinnvoll) */}
          {(tab === 'confirmed' || tab === 'pending') && (
            <div className="flex items-center gap-2 flex-wrap mt-4" data-testid="registrations-inbox-workflow-filters">
              <span className="text-xs uppercase tracking-wide text-slate-500 mr-1">Schnell-Status:</span>
              <WorkflowFilterPill
                active={workflowFilter === 'all'}
                onClick={() => setWorkflowFilter('all')}
                label="Alle"
                testId="registrations-inbox-workflow-filter-all"
              />
              {WORKFLOW_OPTIONS.map((o) => (
                <WorkflowFilterPill
                  key={o.key}
                  active={workflowFilter === o.key}
                  onClick={() => setWorkflowFilter(o.key)}
                  label={o.label}
                  icon={o.icon}
                  cls={o.cls}
                  activeCls={o.active}
                  testId={`registrations-inbox-workflow-filter-${o.key}`}
                />
              ))}
              <WorkflowFilterPill
                active={workflowFilter === 'none'}
                onClick={() => setWorkflowFilter('none')}
                label="Ohne Status"
                testId="registrations-inbox-workflow-filter-none"
              />
              <WorkflowFilterPill
                active={groupWaitlistFilter}
                onClick={() => setGroupWaitlistFilter((v) => !v)}
                label="Warteliste Gruppe"
                icon={Users2}
                cls="border-brand-red/20 text-brand-red bg-brand-red/5 hover:bg-brand-red/10"
                activeCls="bg-brand-red border-brand-red text-white"
                testId="registrations-inbox-group-waitlist-filter"
              />
            </div>
          )}

          <div
            className="mt-4 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-6"
            data-testid="registrations-filter-row"
          >
            {[
              ['student', 'Schüler:in'],
              ['instrument', 'Instrument'],
              ['payer', 'Zahlungspflichtige:r'],
              ['email', 'E-Mail'],
            ].map(([key, placeholder]) => (
              <Input
                key={key}
                value={registrationFilters[key]}
                onChange={(e) => setRegistrationFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                data-testid={`registrations-filter-${key}`}
              />
            ))}
            <Input
              type="date"
              value={registrationFilters.received}
              onChange={(e) => setRegistrationFilters((prev) => ({ ...prev, received: e.target.value }))}
              aria-label="Eingegangen am"
              data-testid="registrations-filter-received"
            />
            <Button
              type="button"
              onClick={() => setAppliedRegistrationFilters({ ...registrationFilters })}
              data-testid="registrations-filter-submit"
            >
              Filtern
            </Button>
          </div>

          {STATUS_TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {loading ? (
                <div className="text-slate-500 flex items-center gap-2 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Laden …</div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center text-slate-500 border border-dashed border-slate-200 rounded-lg" data-testid="registrations-inbox-empty">
                  Keine Einträge in diesem Status.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-3">Schüler:in</th>
                        <th className="text-left px-4 py-3">Instrument</th>
                        <th className="text-left px-4 py-3">Zahler</th>
                        <th className="text-left px-4 py-3">E-Mail</th>
                        <th className="text-left px-4 py-3">Eingegangen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((r, idx) => (
                        <tr
                          key={r.id}
                          className={`${LIST_ROW_CLASS} ${r.is_overdue ? 'bg-brand-red/5' : ''}`}
                          data-testid={`registrations-inbox-row-${r.id}`}
                          {...getRowProps(idx, r)}
                        >
                          <td className="px-4 py-3 font-medium text-slate-900">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{r.student_first_name} {r.student_last_name}</span>
                              {r.is_overdue && (
                                <Badge className="bg-brand-red/10 text-brand-red hover:bg-brand-red/10 gap-1" data-testid={`registrations-inbox-overdue-${r.id}`}>
                                  <AlertTriangle className="h-3 w-3" />
                                  Überfällig
                                </Badge>
                              )}
                              {r.group_priority === 'waitlist' && (
                                <Badge
                                  className={r.group_matched
                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 gap-1'
                                    : 'bg-brand-red/10 text-brand-red hover:bg-brand-red/10 gap-1'}
                                  data-testid={`registrations-inbox-group-badge-${r.id}`}
                                >
                                  <Users2 className="h-3 w-3" />
                                  {r.group_matched ? 'Gruppe · eingeteilt' : 'Warteliste Gruppe'}
                                </Badge>
                              )}
                              {r.workflow_status && (() => {
                                const meta = workflowMeta(r.workflow_status);
                                if (!meta) return null;
                                const Icon = meta.icon;
                                return (
                                  <Badge
                                    className={`gap-1 ${meta.cls}`}
                                    title={meta.label}
                                    data-testid={`registrations-inbox-workflow-tag-${r.id}`}
                                  >
                                    <Icon className="h-3 w-3" /> {meta.label}
                                  </Badge>
                                );
                              })()}
                              {r.teacher_slot_status && (() => {
                                const meta = teacherSlotMeta(r.teacher_slot_status);
                                if (!meta) return null;
                                return (
                                  <Badge className={`gap-1 ${meta.cls}`} title={meta.title} data-testid={`registrations-inbox-teacher-slot-${r.id}`}>
                                    {meta.icon} {meta.label}
                                  </Badge>
                                );
                              })()}
                              {r.parent_choice_status && (() => {
                                const meta = parentChoiceMeta(r.parent_choice_status);
                                if (!meta) return null;
                                return (
                                  <Badge className={`gap-1 ${meta.cls}`} title={meta.title} data-testid={`registrations-inbox-parent-choice-${r.id}`}>
                                    {meta.icon} {meta.label}
                                  </Badge>
                                );
                              })()}
                              {r.needs_manual_intervention && (
                                <Badge className="gap-1 bg-red-100 text-red-900 hover:bg-red-100 border border-red-300"
                                       title={`${r.slot_round || 0} Runden ohne Erfolg — manuelle Zuteilung nötig`}
                                       data-testid={`registrations-inbox-manual-${r.id}`}>
                                  ⚠️ Manuell zuweisen
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">{vokalSpelling(r.category)}</Badge>
                            <span className="ml-2 text-slate-700">{r.instrument}</span>
                          </td>
                          <td className="px-4 py-3">{r.payer_first_name} {r.payer_last_name}</td>
                          <td className="px-4 py-3 text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {r.payer_email}
                              {r.email_verified && (
                                <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-medium" title="E-Mail-Adresse per Code bestätigt" data-testid={`registrations-inbox-email-verified-${r.id}`}>
                                  <ShieldCheck className="h-3.5 w-3.5" />
                                </span>
                              )}
                              {r.phone_verified && (
                                <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-medium" title="Rufnummer per SMS bestätigt" data-testid={`registrations-inbox-phone-verified-${r.id}`}>
                                  <Phone className="h-3 w-3" /><ShieldCheck className="h-3.5 w-3.5" />
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDateTime(r.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
        </>
        )}

      {selected && (
      <div className="max-w-5xl mx-auto" data-testid="registrations-inbox-detail">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-900">Online-Anmeldung</h1>
            <p className="text-slate-600 mt-1">
              {detail ? `Eingegangen ${formatDateTime(detail.created_at)}${detail.tenant_name ? ` · ${detail.tenant_name}` : ''}` : '…'}
            </p>
          </div>

          {detailLoading || !detail ? (
            <div className="flex items-center gap-2 text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Laden …</div>
          ) : (
            <div className="space-y-4" data-testid="registrations-inbox-detail-body">
              <RegistrationMatchPanel
                matches={personMatches?.matches}
                selectedStudentId={matchDecision.studentId}
                selectedGuardianId={matchDecision.guardianId}
                chooseNew={matchDecision.chooseNew}
                onSelectMatch={(match, updates = []) => setMatchDecision((prev) => ({
                  ...prev,
                  chooseNew: false,
                  fieldUpdates: updates,
                  updateTarget: match.entity_type,
                  ...(match.entity_type === 'student'
                    ? { studentId: match.entity_id }
                    : { guardianId: match.entity_id }),
                }))}
                onChooseNew={() => setMatchDecision({
                  ...EMPTY_MATCH_DECISION,
                  chooseNew: true,
                })}
              />
              <DetailBlock title="Schüler:in">
                <div className="font-medium text-slate-900">
                  {detail.payload?.student?.salutation ? `${detail.payload.student.salutation} ` : ''}
                  {detail.payload?.student?.title ? `${detail.payload.student.title} ` : ''}
                  {detail.student_first_name} {detail.student_last_name}
                </div>
                <div className="text-slate-500" data-testid="registration-detail-subject-line">{[vokalSpelling(detail.category), detail.instrument, detail.payload?.student?.lesson_type].filter(Boolean).join(' · ')}</div>
                {(detail.payload?.student?.gender || detail.payload?.student?.birthdate) && (
                  <div className="text-slate-500">
                    {detail.payload?.student?.gender || ''}{detail.payload?.student?.gender && detail.payload?.student?.birthdate ? ' · ' : ''}
                    {detail.payload?.student?.birthdate ? `geb. ${detail.payload.student.birthdate}` : ''}
                  </div>
                )}
                {(detail.payload?.student?.email || detail.payload?.student?.phone) && (
                  <div className="text-slate-500">{[detail.payload?.student?.email, detail.payload?.student?.phone].filter(Boolean).join(' · ')}</div>
                )}
                {(detail.payload?.student?.street || detail.payload?.student?.house_number || detail.payload?.student?.address_extra) && (
                  <div className="text-slate-500" data-testid="registrations-inbox-detail-student-address">
                    {composePostalAddress(detail.payload.student)}
                    {detail.payload.student.district ? ` (${detail.payload.student.district})` : ''}
                    {detail.payload.student.address_same ? ' (wie Zahler)' : ''}
                  </div>
                )}
                {detail.payload?.student?.prior_knowledge && <div className="text-slate-500 mt-1">Vorkenntnisse: {detail.payload.student.prior_knowledge}</div>}
                <div className="text-slate-500">Foto-Erlaubnis: {detail.payload?.student?.photo_consent ? 'ja' : 'nein'}</div>
                {detail.payload?.student?.notes && <div className="text-slate-500 whitespace-pre-line mt-1">{detail.payload.student.notes}</div>}
              </DetailBlock>
              {matching?.slots?.length > 0 && (
                <DetailBlock title={`Passende freie Plätze${matching.instrument ? ` — ${matching.instrument}` : ''}`}>
                  <div className="space-y-1.5" data-testid="registration-matching-slots">
                    {matching.slots.slice(0, 6).map((s) => (
                      <div key={s.course_id} className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 px-2 py-1.5"
                           data-testid={`matching-slot-${s.course_id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-slate-800 truncate">{s.name}</div>
                          <div className="text-[10px] text-slate-500">
                            {[s.day_of_week != null ? ['Mo','Di','Mi','Do','Fr','Sa','So'][s.day_of_week] : null,
                              formatTime(s.start_time), s.teacher_name, s.location_name].filter(Boolean).join(' · ')}
                            {' · '}{s.enrolled}/{s.capacity} belegt
                          </div>
                        </div>
                        <span className="text-[10px] font-bold text-emerald-700 whitespace-nowrap">{s.free} frei</span>
                        {matching.student_id ? (
                          <Button size="sm" className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => assignCourse(s.course_id, s.name)}
                                  data-testid={`matching-slot-assign-${s.course_id}`}>
                            Hier einteilen
                          </Button>
                        ) : (
                          <a href={`/courses/${s.course_id}`} target="_blank" rel="noopener noreferrer"
                             className="text-[10px] font-semibold text-brand-red underline whitespace-nowrap"
                             data-testid={`matching-slot-open-${s.course_id}`}>
                            Kurs öffnen
                          </a>
                        )}
                      </div>
                    ))}
                    {!matching.student_id && (
                      <p className="text-[10px] text-slate-400">Direkte Einteilung erst nach Freigabe der Anmeldung möglich (Schülerdatensatz wird dabei angelegt).</p>
                    )}
                  </div>
                </DetailBlock>
              )}
              {detail.payload?.group && (
                <DetailBlock title="Gruppenunterricht">
                  <div className="font-medium text-slate-900">
                    {{
                      add_students: 'Möchte weitere Schüler anmelden',
                      waitlist: 'Warteliste — wartet auf weiteren passenden Schüler',
                      switch_20min: 'Startet zunächst mit 20 Min Einzelunterricht',
                    }[detail.payload.group.priority] || detail.payload.group.priority}
                  </div>
                  {(detail.payload.group.additional_students || []).map((s, i) => (
                    <div key={`${s.first_name}-${s.last_name}-${i}`} className="text-slate-500">
                      + {s.first_name} {s.last_name}{s.birthdate ? ` (${s.birthdate})` : ''}
                    </div>
                  ))}
                  {detail.payload.group.priority === 'waitlist' && (
                    <div className={`text-xs mt-1 ${detail.payload.group.waitlist_notified ? 'text-emerald-600' : 'text-brand-red'}`}>
                      {detail.payload.group.waitlist_notified
                        ? 'Benachrichtigung über passenden Schüler wurde ausgelöst'
                        : 'Wartet — noch kein passender Schüler gefunden'}
                    </div>
                  )}
                  {detail.payload.group.proposed_slot && (
                    <div className="mt-2 text-sm bg-emerald-50 border border-emerald-200 rounded-md p-2 text-emerald-800" data-testid="registration-detail-group-proposed-slot">
                      Automatische Einteilung: {formatDate(detail.payload.group.proposed_slot.date)} · {formatTimeRangeUhr(detail.payload.group.proposed_slot.start_time, detail.payload.group.proposed_slot.end_time)} bei {detail.payload.group.proposed_slot.teacher_name}
                    </div>
                  )}
                </DetailBlock>
              )}
              {(detail.payload?.teaching_type || detail.payload?.course_offer || detail.payload?.location) && (
                <DetailBlock title="Unterricht">
                  {detail.payload?.teaching_type && (
                    <div className="text-slate-700" data-testid="registration-detail-teaching-type">
                      <span className="text-slate-500">Unterrichtsart: </span>{detail.payload.teaching_type}
                    </div>
                  )}
                  {detail.payload?.course_offer && (
                    <div className="text-slate-700" data-testid="registration-detail-course-offer">
                      <span className="text-slate-500">Kursangebot: </span>{formatCourseOfferLine(detail.payload.course_offer)}
                      {detail.payload.course_offer.source === 'manual' ? ' (manuell gepflegt)' : ''}
                    </div>
                  )}
                  {detail.payload?.location && (
                    <div className="text-slate-700" data-testid="registration-detail-location">
                      <span className="text-slate-500">Unterrichtsort: </span>{detail.payload.location.name}
                      {detail.payload.location.address ? ` — ${detail.payload.location.address}` : ''}
                    </div>
                  )}
                </DetailBlock>
              )}
              {detail.payload?.guardian && !detail.payload.guardian.same_as_payer && (
                <DetailBlock title="Erziehungsberechtigte:r">
                  <div className="font-medium text-slate-900" data-testid="registration-detail-guardian">
                    {detail.payload.guardian.salutation ? `${detail.payload.guardian.salutation} ` : ''}
                    {detail.payload.guardian.title ? `${detail.payload.guardian.title} ` : ''}
                    {detail.payload.guardian.first_name} {detail.payload.guardian.last_name}
                  </div>
                  {(detail.payload.guardian.email || detail.payload.guardian.phone) && (
                    <div className="text-slate-500">{[detail.payload.guardian.email, detail.payload.guardian.phone].filter(Boolean).join(' · ')}</div>
                  )}
                  {(detail.payload.guardian.street || detail.payload.guardian.house_number || detail.payload.guardian.address_extra) && (
                    <div className="text-slate-500" data-testid="registrations-inbox-detail-guardian-address">
                      {composePostalAddress(detail.payload.guardian)}
                      {detail.payload.guardian.district ? ` (${detail.payload.guardian.district})` : ''}
                      {detail.payload.guardian.address_same ? ' (wie Zahler)' : ''}
                    </div>
                  )}
                </DetailBlock>
              )}
              <DetailBlock title="Zahler">
                <div className="font-medium text-slate-900">
                  {detail.payload?.payer?.salutation ? `${detail.payload.payer.salutation} ` : ''}
                  {detail.payload?.payer?.title ? `${detail.payload.payer.title} ` : ''}
                  {detail.payer_first_name} {detail.payer_last_name}
                </div>
                <div className="text-slate-500 flex items-center flex-wrap gap-x-1.5">
                  <span className="inline-flex items-center gap-1">
                    {detail.payer_email}
                    {detail.payload?.email_verified && (
                      <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-medium" data-testid="registration-detail-email-verified">
                        <ShieldCheck className="h-3.5 w-3.5" /> bestätigt
                      </span>
                    )}
                  </span>
                  {detail.payload?.payer?.phone && (
                    <span className="inline-flex items-center gap-1">
                      · {detail.payload.payer.phone}
                      {detail.payload?.phone_verified && (
                        <span className="inline-flex items-center gap-0.5 text-emerald-600 text-xs font-medium" data-testid="registration-detail-phone-verified">
                          <ShieldCheck className="h-3.5 w-3.5" /> bestätigt
                        </span>
                      )}
                    </span>
                  )}
                  {(detail.payload?.payer?.additional_phones || []).map((ap, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-slate-500" data-testid={`registration-detail-extra-phone-${i}`}>
                      · {ap.number} <span className="text-[11px] text-slate-400">({ap.type})</span>
                    </span>
                  ))}
                </div>
                <div className="text-slate-500" data-testid="registrations-inbox-detail-payer-address">
                  {composePostalAddress(detail.payload?.payer)}
                  {detail.payload?.payer?.district ? ` (${detail.payload.payer.district})` : ''}
                </div>
                {(detail.payload?.payer?.gender || detail.payload?.payer?.birthdate) && (
                  <div className="text-slate-500">
                    {detail.payload?.payer?.gender || ''}{detail.payload?.payer?.gender && detail.payload?.payer?.birthdate ? ' · ' : ''}
                    {detail.payload?.payer?.birthdate ? `geb. ${detail.payload.payer.birthdate}` : ''}
                  </div>
                )}
              </DetailBlock>
              {detail.payload?.muensterland_card && (
                <DetailBlock title="Münsterland-Card">
                  <div className="font-medium text-slate-900" data-testid="registrations-inbox-detail-muensterland-card">
                    {detail.payload.muensterland_card.has_card && detail.payload.muensterland_card.number
                      ? detail.payload.muensterland_card.number
                      : 'nein'}
                  </div>
                </DetailBlock>
              )}
              <DetailBlock title="SEPA">
                <div className="font-medium text-slate-900">{detail.payload?.sepa?.account_holder || '—'}</div>
                {detail.payload?.sepa?.iban && <div className="text-slate-500 font-mono">{detail.payload.sepa.iban}</div>}
                {detail.payload?.sepa?.bic && <div className="text-slate-500 font-mono">{detail.payload.sepa.bic}</div>}
              </DetailBlock>
              {Array.isArray(detail.payload?.custom_fields) && detail.payload.custom_fields.length > 0 && (
                <DetailBlock title="Zusätzliche Angaben">
                  {detail.payload.custom_fields.map((cf) => (
                    <div key={cf.id} className="text-slate-700">
                      <span className="text-slate-500">{cf.label}: </span>
                      {cf.type === 'checkbox' ? (cf.value ? 'Ja' : 'Nein') : (String(cf.value ?? '') || '—')}
                    </div>
                  ))}
                </DetailBlock>
              )}
              {formatAvailability(detail.payload?.availability) && (
                <DetailBlock title="Verfügbarkeit">
                  <div className="font-medium text-slate-900" data-testid="registrations-inbox-detail-availability">
                    {formatAvailability(detail.payload.availability)}
                  </div>
                </DetailBlock>
              )}
              <DetailBlock title="Wunschtermin">
                {!detail.payload?.preferred_slot && detail.payload?.group?.proposed_slot && (
                  <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2 mb-2" data-testid="registrations-inbox-detail-group-slot-hint">
                    Bei Genehmigung wird die automatische Gruppen-Einteilung
                    ({formatDate(detail.payload.group.proposed_slot.date)} · {formatTimeRangeUhr(detail.payload.group.proposed_slot.start_time, detail.payload.group.proposed_slot.end_time)}
                    bei {detail.payload.group.proposed_slot.teacher_name}) als Gruppenkurs + Belegung übernommen.
                  </div>
                )}
                {detail.payload?.preferred_slot ? (
                  <>
                    <div className="font-medium text-slate-900" data-testid="registrations-inbox-detail-slot">
                      {formatDate(detail.payload.preferred_slot.date)} · {formatTimeRangeUhr(detail.payload.preferred_slot.start_time, detail.payload.preferred_slot.end_time)}
                    </div>
                    <div className="text-slate-500">
                      {detail.payload.preferred_slot.duration_minutes || 45} Min · Lehrkraft-ID {detail.payload.preferred_slot.teacher_id}
                    </div>
                    {detail.teacher_slot_status && (() => {
                      const meta = teacherSlotMeta(detail.teacher_slot_status);
                      if (!meta) return null;
                      return (
                        <div className="mt-2" data-testid="registrations-inbox-detail-teacher-slot-status">
                          <Badge className={`gap-1 ${meta.cls}`}>{meta.icon} {meta.label}</Badge>
                          {detail.teacher_slot_status === 'confirmed' && detail.teacher_slot_confirmed_at && (
                            <span className="ml-2 text-xs text-slate-500">
                              {formatDateTime(detail.teacher_slot_confirmed_at)}
                            </span>
                          )}
                          {detail.teacher_slot_status === 'declined' && detail.teacher_slot_decline_reason && (
                            <div className="mt-1 text-xs text-rose-700 italic">
                              Grund: „{detail.teacher_slot_decline_reason}"
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {detail.parent_choice_status && (() => {
                      const meta = parentChoiceMeta(detail.parent_choice_status);
                      if (!meta) return null;
                      return (
                        <div className="mt-2" data-testid="registrations-inbox-detail-parent-choice">
                          <Badge className={`gap-1 ${meta.cls}`}>{meta.icon} {meta.label}</Badge>
                          {detail.parent_choice_reason && (
                            <div className="mt-1 text-xs text-slate-700 italic">
                              Eltern-Antwort: „{detail.parent_choice_reason}"
                            </div>
                          )}
                          {detail.final_slot && (
                            <div className="mt-1 text-xs text-emerald-700">
                              Final gewählter Termin: {formatDate(detail.final_slot.date)} · {formatTime(detail.final_slot.start_time)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {['confirmed', 'pending'].includes(detail.status) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {(!detail.teacher_slot_status || detail.teacher_slot_status === 'pending'
                          || detail.teacher_slot_status === 'declined') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startTeacherCoordination(false)}
                            disabled={coordBusy}
                            data-testid="registrations-inbox-start-teacher-coord"
                          >
                            <UserCog className="h-3.5 w-3.5 mr-1" />
                            {detail.teacher_slot_status === 'pending' ? 'Lehrkraft erneut anfragen' : 'Lehrkraft anfragen'}
                          </Button>
                        )}
                        {['proposed_alternatives', 'declined', 'confirmed'].includes(detail.teacher_slot_status) && (
                          <Button
                            size="sm"
                            onClick={forwardToParent}
                            disabled={coordBusy}
                            data-testid="registrations-inbox-forward-to-parent"
                          >
                            <Send className="h-3.5 w-3.5 mr-1" /> An Anmelder weiterleiten
                          </Button>
                        )}
                      </div>
                    )}
                    {detail.needs_manual_intervention && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-lg" data-testid="registrations-inbox-manual-intervention">
                        <div className="text-sm font-semibold text-red-900">⚠️ Manuelle Zuweisung erforderlich</div>
                        <div className="text-xs text-red-700 mt-1">
                          Nach {detail.slot_round} Verhandlungsrunden konnte kein Termin automatisch gefunden werden.
                          Bitte persönlich mit Eltern und einer geeigneten Lehrkraft abstimmen.
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div id="registrations-assign-slot" data-testid="registrations-inbox-detail-fallback">
                    {detail.payload?.fallback_contact ? (
                      <div className="text-slate-700 mb-3">
                        Eltern wünschen Rückruf. Nach dem Telefonat Termin eintragen und die Lehrkraft anfragen.
                      </div>
                    ) : formatAvailability(detail.payload?.availability) ? (
                      <div className="text-slate-500 mb-3">Noch kein fester Termin — anhand der Verfügbarkeit zuordnen.</div>
                    ) : (
                      <div className="text-slate-500 mb-3">Kein Wunschtermin angegeben — hier zuordnen.</div>
                    )}
                    {['confirmed', 'pending'].includes(detail.status) && (
                      <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <label className="block text-xs text-slate-600">
                          Lehrkraft
                          <select
                            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
                            value={coordTeacherId}
                            onChange={(e) => setCoordTeacherId(e.target.value)}
                            data-testid="registrations-assign-teacher"
                          >
                            <option value="">Bitte wählen</option>
                            {coordTeachers.map((t) => (
                              <option key={t.id} value={t.id}>{t.last_name}, {t.first_name}</option>
                            ))}
                          </select>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block text-xs text-slate-600">
                            Datum
                            <DateInput
                              className="mt-1"
                              value={coordDate}
                              onChange={(e) => setCoordDate(e.target.value)}
                              data-testid="registrations-assign-date"
                            />
                          </label>
                          <label className="block text-xs text-slate-600">
                            Uhrzeit
                            <TimeInput
                              className="mt-1"
                              value={coordStart}
                              onChange={(e) => setCoordStart(e.target.value)}
                              data-testid="registrations-assign-time"
                            />
                          </label>
                        </div>
                        <label className="block text-xs text-slate-600">
                          Dauer (Minuten)
                          <Input
                            type="number"
                            min={10}
                            max={240}
                            className="mt-1 h-9 w-28"
                            value={coordDuration}
                            onChange={(e) => setCoordDuration(e.target.value)}
                            data-testid="registrations-assign-duration"
                          />
                        </label>
                        <Button
                          size="sm"
                          onClick={() => startTeacherCoordination(true)}
                          disabled={coordBusy}
                          data-testid="registrations-inbox-start-teacher-coord-assign"
                        >
                          <UserCog className="h-3.5 w-3.5 mr-1" />
                          Lehrkraft anfragen
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </DetailBlock>
              <DetailBlock title="Bestätigungs-E-Mail">
                <div className="space-y-1" data-testid="registration-confirmation-email">
                  {detail.confirmation_email_status === 'sent' && (
                    <div className="text-emerald-700">
                      Versendet an {detail.confirmation_email_recipient || detail.payer_email}
                      {detail.confirmation_email_at ? ` · ${formatDateTime(detail.confirmation_email_at)}` : ''}
                    </div>
                  )}
                  {detail.confirmation_email_status === 'failed' && (
                    <div className="text-red-700">
                      Versand fehlgeschlagen
                      {detail.confirmation_email_recipient ? ` an ${detail.confirmation_email_recipient}` : ''}
                      {detail.confirmation_email_error ? `: ${detail.confirmation_email_error}` : ''}
                    </div>
                  )}
                  {!detail.confirmation_email_status && (
                    <div className="text-slate-500">
                      Noch kein Versandprotokoll. Die Bestätigung geht an die Zahler-Adresse {detail.payer_email}.
                    </div>
                  )}
                  <p className="text-xs text-slate-400">
                    Text unter{' '}
                    <a href="/correspondence" className="text-brand-red hover:underline">Korrespondenz</a>
                    {' '}→ Systemvorlage „Anmeldebestätigung Online-Anmeldung“.
                  </p>
                  {detail.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1"
                      onClick={resendConfirmation}
                      disabled={resendingConfirm}
                      data-testid="registrations-inbox-resend-confirmation"
                    >
                      {resendingConfirm ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                      Bestätigung erneut senden
                    </Button>
                  )}
                </div>
              </DetailBlock>
              <DetailBlock title="Korrespondenzverlauf">
                {(comms?.items || []).length === 0 ? (
                  <div className="text-slate-500" data-testid="registration-communications-empty">Noch keine versendeten Nachrichten zu diesem Vorgang.</div>
                ) : (
                  <ol className="space-y-2" data-testid="registration-communications">
                    {comms.items.map((item, idx) => (
                      <li key={`${item.channel}-${item.at}-${idx}`} className="text-sm border-l-2 border-slate-200 pl-2">
                        <div className="flex flex-wrap items-center gap-x-2">
                          <span className="font-medium text-slate-800">{item.kind_label}</span>
                          <span className="text-[10px] uppercase text-slate-400">{item.channel}</span>
                          <span className={item.status === 'sent' || item.status === 'delivered' ? 'text-emerald-700 text-xs' : 'text-red-700 text-xs'}>
                            {statusLabel(item.status)}
                          </span>
                        </div>
                        <div className="text-slate-500 text-xs">
                          {item.at ? formatDateTime(item.at) : ''}{item.recipient ? ` · ${item.recipient}` : ''}
                        </div>
                        {item.subject && <div className="text-slate-600 text-xs truncate">{item.subject}</div>}
                        {item.error && <div className="text-red-600 text-xs">{item.error}</div>}
                      </li>
                    ))}
                  </ol>
                )}
              </DetailBlock>
              <DetailBlock title="Anmeldeprotokoll (unveränderlich)">
                {(() => {
                  const proto = detail.payload?._protocol || {};
                  const snap = proto.snapshot || detail.payload || {};
                  const consents = snap.consents || detail.payload?.consents || {};
                  return (
                    <div className="space-y-1 text-slate-600" data-testid="registration-protocol">
                      <div>Eingegangen: {formatDateTime(proto.received_at || detail.created_at)}</div>
                      <div>Mandant: {proto.tenant_name || detail.tenant_name || '—'}</div>
                      <div>Fach / Angebot: {[proto.instrument || detail.instrument, proto.category || detail.category, snap.teaching_type, snap.course_offer ? formatCourseOfferLine(snap.course_offer) : ''].filter(Boolean).join(' · ') || '—'}</div>
                      <div>
                        Bestätigte Erklärungen:
                        {' '}
                        {[consents.agb && 'AGB', consents.datenschutz && 'Datenschutz', consents.widerruf && 'Widerruf'].filter(Boolean).join(', ') || '—'}
                      </div>
                      {detail.user_agent && <div className="text-xs text-slate-400 truncate">Gerät: {detail.user_agent}</div>}
                      <details className="mt-2">
                        <summary className="cursor-pointer text-xs text-brand-red">Ursprüngliche Angaben anzeigen</summary>
                        <pre className="mt-2 text-[10px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto max-h-64">
                          {JSON.stringify(snap, null, 2)}
                        </pre>
                      </details>
                    </div>
                  );
                })()}
              </DetailBlock>
              <DetailBlock title="Status">
                <StatusBadge status={detail.status} />
                {detail.rejection_reason && (
                  <div className="mt-2 text-sm text-red-600">Grund: {detail.rejection_reason}</div>
                )}
                {detail.admin_notes && (
                  <div className="mt-2 text-sm text-slate-500">Notizen: {detail.admin_notes}</div>
                )}
                {detail.ip_address && <div className="mt-2 text-xs text-slate-400">Eingereicht von IP {detail.ip_address}</div>}

                {(detail.status === 'confirmed' || detail.status === 'pending') && (
                  <div className="mt-3 pt-3 border-t border-slate-200" data-testid="registrations-inbox-workflow">
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Schnell-Status</div>
                    <div className="flex flex-wrap gap-2">
                      {WORKFLOW_OPTIONS.map((o) => {
                        const isActive = detail.workflow_status === o.key;
                        const Icon = o.icon;
                        return (
                          <button
                            key={o.key}
                            type="button"
                            onClick={() => setWorkflowStatus(isActive ? null : o.key)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${isActive ? o.active : o.cls}`}
                            data-testid={`registrations-inbox-workflow-${o.key}`}
                          >
                            <Icon className="h-3 w-3" /> {o.label}
                          </button>
                        );
                      })}
                      {detail.workflow_status && (
                        <button
                          type="button"
                          onClick={() => setWorkflowStatus(null)}
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200"
                          data-testid="registrations-inbox-workflow-clear"
                        >
                          <XCircle className="h-3 w-3" /> Status zurücksetzen
                        </button>
                      )}
                    </div>
                    {detail.workflow_status && detail.workflow_status_at && (
                      <div className="text-xs text-slate-400 mt-2" data-testid="registrations-inbox-workflow-timestamp">
                        Gesetzt am {formatDateTime(detail.workflow_status_at)}
                      </div>
                    )}
                  </div>
                )}
              </DetailBlock>

              {(detail.created_student_id || detail.created_guardian_id || detail.created_family_id) && (
                <DetailBlock title="Automatisch angelegte Stammdaten">
                  <div className="space-y-1">
                    {detail.created_student_id && (
                      <div data-testid="registrations-inbox-link-student">
                        <a href={`/students/${detail.created_student_id}`} className="text-brand-red hover:underline">
                          → Schüler:in öffnen
                        </a>
                      </div>
                    )}
                    {detail.created_guardian_id && (
                      <div data-testid="registrations-inbox-link-guardian">
                        <a href={`/guardians`} className="text-brand-red hover:underline">
                          → Zahler in der Liste suchen
                        </a>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{detail.created_guardian_id}</span>
                      </div>
                    )}
                    {detail.created_family_id && (
                      <div data-testid="registrations-inbox-link-family">
                        <a href={`/families`} className="text-brand-red hover:underline">
                          → Familie öffnen
                        </a>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{detail.created_family_id}</span>
                      </div>
                    )}
                    {detail.created_contract_id && (
                      <div data-testid="registrations-inbox-link-contract">
                        <a href={`/contracts/${detail.created_contract_id}`} className="text-brand-red hover:underline font-medium">
                          → Vertrag öffnen
                        </a>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{detail.created_contract_id}</span>
                      </div>
                    )}
                    {detail.created_course_id && (
                      <div data-testid="registrations-inbox-link-course">
                        <a href={`/courses/${detail.created_course_id}`} className="text-brand-red hover:underline font-medium">
                          → Kurs (Stundenplan) öffnen
                        </a>
                        <span className="text-xs text-slate-400 ml-2 font-mono">{detail.created_course_id}</span>
                      </div>
                    )}
                    {detail.created_enrollment_id && (
                      <div data-testid="registrations-inbox-link-enrollment" className="text-xs text-slate-500 font-mono">
                        Enrollment: {detail.created_enrollment_id}
                      </div>
                    )}
                  </div>
                </DetailBlock>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <Button variant="outline" onClick={closeDetail} data-testid="registrations-inbox-close">Schließen</Button>
            {detail?.status === 'approved' && detail.payload?.preferred_slot && (
              <Button variant="outline" onClick={openSlotMailDialog} data-testid="registrations-inbox-slotmail-open">
                <Mail className="h-4 w-4 mr-1" /> Bestätigungs-E-Mail senden
              </Button>
            )}
            {(detail?.status === 'confirmed' || detail?.status === 'pending') && (
              <>
                <Button
                  variant="outline"
                  onClick={openUnterrichtsverwaltung}
                  data-testid="registrations-inbox-open-uv"
                >
                  <ExternalLink className="h-4 w-4 mr-1" /> Unterrichtsverwaltung
                </Button>
                <Button
                  variant="outline"
                  onClick={openManualEnroll}
                  data-testid="registrations-inbox-manual-enroll"
                >
                  <ClipboardList className="h-4 w-4 mr-1" /> Manuell Einteilen
                </Button>
              </>
            )}
            {detail?.status === 'confirmed' && (
              <>
                <Button variant="destructive" onClick={() => setRejectOpen(true)} data-testid="registrations-inbox-reject-open">
                  <XCircle className="h-4 w-4 mr-1" /> Ablehnen
                </Button>
                <Button onClick={openApproveDialog} data-testid="registrations-inbox-approve-open">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Genehmigen
                </Button>
              </>
            )}
            {detail?.status === 'pending' && (
              <Button variant="destructive" onClick={() => setRejectOpen(true)} data-testid="registrations-inbox-reject-open-pending">
                <XCircle className="h-4 w-4 mr-1" /> Ablehnen
              </Button>
            )}
          </div>
      </div>
      )}
      </div>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent
          className="max-w-xl max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto]"
          data-testid="registrations-inbox-approve-dialog"
        >
          <DialogHeader>
            <DialogTitle>Anmeldung genehmigen</DialogTitle>
            <DialogDescription>
              Wir legen automatisch Schüler:in, Zahler, Familie und SEPA-Mandat an.
              Zusätzlich kannst du hier direkt einen Vertrag erstellen.
            </DialogDescription>
          </DialogHeader>

          {approveOptions === null ? (
            <div className="flex items-center gap-2 text-slate-500 py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Vorschläge laden …
            </div>
          ) : (
            <div className="space-y-4 overflow-y-auto pr-1" data-testid="registrations-inbox-approve-body">
              <label className="flex items-center gap-2 text-sm" data-testid="registrations-inbox-approve-skip-row">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={approveSkipContract}
                  onChange={(e) => setApproveSkipContract(e.target.checked)}
                  data-testid="registrations-inbox-approve-skip"
                />
                Nur Stammdaten anlegen, keinen Vertrag erstellen
              </label>

              {!approveSkipContract && (
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Tarif *</label>
                    <select
                      value={approveTariffId}
                      onChange={(e) => setApproveTariffId(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="registrations-inbox-approve-tariff"
                    >
                      <option value="">— bitte wählen —</option>
                      {approveOptions.tariffs.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {formatDeNumber(t.amount)} €
                        </option>
                      ))}
                    </select>
                    {approveOptions.tariffs.length === 0 && (
                      <div className="text-xs text-brand-red mt-1">
                        Keine aktiven Tarife im Mandanten — bitte einen Tarif anlegen oder „nur Stammdaten" wählen.
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Lehrkraft (optional)</label>
                    <select
                      value={approveTeacherId}
                      onChange={(e) => setApproveTeacherId(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="registrations-inbox-approve-teacher"
                    >
                      <option value="">— keine feste Zuordnung —</option>
                      {approveOptions.teachers.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.last_name}, {t.first_name}
                          {t.subjects?.length ? ` · ${t.subjects.join(', ')}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Unterrichtsraum (optional)</label>
                    <select
                      value={approveRoomId}
                      onChange={(e) => setApproveRoomId(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="registrations-inbox-approve-room"
                    >
                      <option value="">— kein Raum zugeordnet —</option>
                      {(approveOptions.rooms || []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}{r.location_name ? ` · ${r.location_name}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-slate-400 mt-1">Wird am Kurs hinterlegt und in der Willkommens-Mail genannt.</div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Vertragsbeginn</label>
                    <DateInput
                      
                      value={approveStartDate}
                      onChange={(e) => setApproveStartDate(e.target.value)}
                      data-testid="registrations-inbox-approve-startdate"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">Notiz (optional)</label>
                <Textarea
                  rows={2}
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  data-testid="registrations-inbox-approve-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} data-testid="registrations-inbox-approve-cancel">Abbrechen</Button>
            <Button
              onClick={approve}
              disabled={approving || (!approveSkipContract && !approveTariffId)}
              data-testid="registrations-inbox-approve-confirm"
            >
              {approving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              {approveSkipContract ? 'Nur genehmigen' : 'Genehmigen & Vertrag anlegen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="registrations-inbox-reject-dialog">
          <DialogHeader>
            <DialogTitle>Anmeldung ablehnen</DialogTitle>
            <DialogDescription>Bitte gib einen Grund an — er wird intern gespeichert.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="z.B. Kein Platz mehr, Schüler bereits angemeldet, …" data-testid="registrations-inbox-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="registrations-inbox-reject-cancel">Abbrechen</Button>
            <Button variant="destructive" onClick={reject} disabled={approving} data-testid="registrations-inbox-reject-confirm">
              {approving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot-Confirmation Mail Preview Dialog (Phase F4) */}
      <Dialog open={slotMailOpen} onOpenChange={setSlotMailOpen}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto]"
          data-testid="registrations-inbox-slotmail-dialog"
        >
          <DialogHeader>
            <DialogTitle>Bestätigungs-E-Mail mit iCal-Einladung senden</DialogTitle>
            <DialogDescription>
              Voransicht gemäß Mail-Versand-Policy. Eltern und Lehrkraft erhalten eine
              Kalender-Einladung (.ics) mit wöchentlich-wiederkehrendem Termin.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto pr-1" data-testid="registrations-inbox-slotmail-body">
          {slotMailLoading && (
            <div className="flex items-center gap-2 text-slate-500 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Voransicht wird erstellt …
            </div>
          )}
          {!slotMailLoading && slotMailPreview && (
            <div className="space-y-4">
              {slotMailPreview.parent && (
                <MailPreviewCard
                  title="Eltern / Zahler"
                  data={slotMailPreview.parent}
                  enabled={slotMailSendParent}
                  onToggle={(v) => setSlotMailSendParent(v)}
                  testId="slotmail-parent"
                />
              )}
              {slotMailPreview.teacher ? (
                <MailPreviewCard
                  title="Lehrkraft"
                  data={slotMailPreview.teacher}
                  enabled={slotMailSendTeacher}
                  onToggle={(v) => setSlotMailSendTeacher(v)}
                  testId="slotmail-teacher"
                />
              ) : (
                <div className="border border-brand-red/20 bg-brand-red/5 rounded-lg p-3 text-sm text-slate-800" data-testid="slotmail-teacher-missing">
                  Lehrkraft hat keine E-Mail-Adresse hinterlegt — die E-Mail kann nur an Eltern gesendet werden.
                </div>
              )}
            </div>
          )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotMailOpen(false)} data-testid="slotmail-cancel">
              Abbrechen
            </Button>
            <Button
              onClick={sendSlotMail}
              disabled={slotMailSending || slotMailLoading}
              data-testid="slotmail-send-btn"
            >
              {slotMailSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Jetzt senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function MailPreviewCard({ title, data, enabled, onToggle, testId }) {
  return (
    <div className={`border rounded-lg p-3 ${enabled ? 'border-brand-red/30 bg-white' : 'border-slate-200 bg-slate-50/60 opacity-70'}`} data-testid={`${testId}-card`}>
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          className="h-4 w-4"
          data-testid={`${testId}-toggle`}
        />
        <span className="font-medium text-slate-800">{title}</span>
        <span className="text-xs text-slate-500 font-mono">{data.to}</span>
      </label>
      <div className="text-sm">
        <div className="text-slate-500 text-xs">Betreff</div>
        <div className="font-medium text-slate-900 mb-2" data-testid={`${testId}-subject`}>{data.subject}</div>
        <div className="text-slate-500 text-xs">Vorschau (HTML)</div>
        <div
          className="max-h-56 overflow-y-auto border border-slate-200 rounded bg-white p-3 text-sm prose prose-sm"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.html || '') }}
          data-testid={`${testId}-html`}
        />
        <div className="text-xs text-brand-red mt-2">📎 Anhang: {data.attachment_name}</div>
      </div>
    </div>
  );
}

function WorkflowFilterPill({ active, onClick, label, icon: Icon, cls, activeCls, testId }) {
  const neutralActive = 'bg-slate-900 text-white hover:bg-slate-800';
  const neutralInactive = 'bg-slate-100 text-slate-700 hover:bg-slate-200';
  const inactive = cls || neutralInactive;
  const activeClass = activeCls || neutralActive;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${active ? activeClass : inactive}`}
      data-testid={testId}
    >
      {Icon && <Icon className="h-3 w-3" />} {label}
    </button>
  );
}

function FunnelStat({ icon: Icon, label, value, active, onClick, tone, testId }) {
  const toneMap = {
    sky:    { idle: 'bg-white border-sky-200 hover:border-sky-400',       active: 'bg-sky-600 text-white border-sky-600',       iconCls: 'text-sky-600' },
    indigo: { idle: 'bg-white border-brand-red/20 hover:border-brand-red/40', active: 'bg-brand-red text-white border-brand-red', iconCls: 'text-brand-red' },
    slate:  { idle: 'bg-white border-slate-200 hover:border-brand-red/40',   active: 'bg-brand-red text-white border-brand-red',   iconCls: 'text-slate-500' },
    red:    { idle: 'bg-white border-red-200 hover:border-red-400',       active: 'bg-red-600 text-white border-red-600',       iconCls: 'text-red-600' },
  };
  const cfg = toneMap[tone] || toneMap.sky;
  const cls = active ? cfg.active : cfg.idle;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left border rounded-lg p-3 transition-all ${cls}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${active ? 'text-white' : cfg.iconCls}`} />
        <span className={`text-xs font-medium ${active ? 'text-white/90' : 'text-slate-600'}`}>{label}</span>
      </div>
      <div className={`text-2xl font-bold ${active ? 'text-white' : 'text-slate-900'}`} data-testid={`${testId}-value`}>{value}</div>
    </button>
  );
}

function FunnelMiniPill({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
        active ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

function DetailBlock({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <div className="text-sm text-slate-800 space-y-0.5">{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Wartet auf E-Mail-Bestätigung', cls: 'bg-slate-100 text-slate-700' },
    confirmed: { label: 'Bestätigt', cls: 'bg-brand-red/10 text-brand-red' },
    approved: { label: 'Genehmigt', cls: 'bg-emerald-100 text-emerald-800' },
    rejected: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-800' },
  };
  const s = map[status] || { label: statusLabel(status), cls: 'bg-slate-100 text-slate-800' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${s.cls}`}>{s.label}</span>;
}


function teacherSlotMeta(status) {
  if (!status) return null;
  const map = {
    pending: {
      label: 'Lehrer informiert',
      title: 'Einladung wurde an die Lehrkraft gesendet, Antwort steht noch aus.',
      cls: 'bg-sky-100 text-sky-800 hover:bg-sky-100',
      icon: '📧',
    },
    confirmed: {
      label: 'Lehrer hat bestätigt',
      title: 'Lehrkraft hat den Slot per Magic-Link bestätigt.',
      cls: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
      icon: '✅',
    },
    proposed_alternatives: {
      label: 'Lehrer schlägt Alt-Termine vor',
      title: 'Lehrkraft hat alternative Termine an die Eltern gesendet, wartet auf Eltern-Antwort.',
      cls: 'bg-brand-red/10 text-brand-red hover:bg-brand-red/10',
      icon: '📅',
    },
    declined: {
      label: 'Lehrer hat abgelehnt',
      title: 'Lehrkraft hat den Slot abgelehnt — Alt-Lehrer wurde vorgeschlagen.',
      cls: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
      icon: '❌',
    },
  };
  return map[status] || null;
}

function parentChoiceMeta(status) {
  if (!status) return null;
  const map = {
    pending: {
      label: 'Eltern wählen',
      title: 'Eltern-Magic-Link versendet, Antwort der Eltern steht noch aus.',
      cls: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
      icon: '⏳',
    },
    accepted: {
      label: 'Eltern haben bestätigt',
      title: 'Eltern haben den Vorschlag angenommen.',
      cls: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
      icon: '✅',
    },
    requested_other_teacher: {
      label: 'Eltern wünschen anderen Lehrer',
      title: 'Eltern lehnen den Alt-Lehrer ab — manuelle Zuteilung nötig.',
      cls: 'bg-brand-red/10 text-brand-red hover:bg-brand-red/10',
      icon: '🔄',
    },
    declined: {
      label: 'Eltern haben abgelehnt',
      title: 'Eltern haben den Vorschlag abgelehnt — bitte persönlich kontaktieren.',
      cls: 'bg-rose-100 text-rose-800 hover:bg-rose-100',
      icon: '❌',
    },
  };
  return map[status] || null;
}
